import { faClose, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, IconButton, Popover, Typography, Paper, Alert } from "@mui/material";
import React, { useState } from "react";
import { FileIcon, DefaultExtensionType } from "react-file-icon";
import { useTranslation } from "react-i18next";

import { State, Props } from "./interfaces";
import { Container, DeletButton, StyledFontAwesomeIcon } from "./styles";

import { fileSize } from "../../../containers/Document/NewFile/utils";
import { customFileIconStyles } from "../../../reusable-components/DefaultFileIconStyles/default-file-icon-styles";
import DocumentViewer from "../../../reusable-components/DocumentViewer/DocumentViewer";
import { READABLE_FILE_TYPES, READABLE_IMAGE_TYPES } from "../../../reusable-components/DocumentViewer/fileTypes";
import { handleDownloadFile } from "../../../services/documents/document-files-api.service";
import { getFileExtension } from "../../../utils/files-utils/get-file-extension";

import { intlFormatDateWithHour } from "../../../utils/time-utils";

const SimpleDocumentView = ({
  document,
  handleDeleteFile,
  simpleViewAlert,
  documentIsReadOnly,
  checkIfFileDeletable,
  isGenericDocument,
}: Props) => {
  const { t } = useTranslation();

  const [state, setState] = useState<State>({
    isViewingFile: false,
    file: null,
  });

  return (
    <Container>
      {!!simpleViewAlert && (
        <Alert
          severity={simpleViewAlert.severity}
          style={{ width: "100%" }}
        >
          {simpleViewAlert.message}{" "}
        </Alert>
      )}

      {document.files.map((file) => {
        const fileExtension = getFileExtension(file.name).toLowerCase();
        const fileExtensionRegEx = new RegExp(fileExtension, "ig");
        return (
          <Paper
            key={file.id}
            style={{
              width: "47%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "5px",
            }}
            title={file.name}
          >
            <Grid
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                overflowWrap: "anywhere",
              }}
            >
              {!READABLE_FILE_TYPES.includes(fileExtension) && !READABLE_IMAGE_TYPES.includes(fileExtension) && (
                <IconButton
                  title={`"${file.name}" ${fileSize(file.size)} ${t("common_words.user")} ${
                    file.createdByUser?.name ?? file.createdByUser?.email ?? ""
                  } ${intlFormatDateWithHour(file.createdAt)}`}
                  onClick={() => handleDownloadFile(file)}
                  style={{ width: "25px" }}
                >
                  <FileIcon
                    extension={fileExtension}
                    {...customFileIconStyles[fileExtension as DefaultExtensionType]}
                    radius={3}
                  />
                </IconButton>
              )}
              {(READABLE_FILE_TYPES.includes(fileExtension) || READABLE_IMAGE_TYPES.includes(fileExtension)) && (
                <IconButton
                  onClick={() => {
                    setState((state) => ({
                      ...state,
                      isViewingFile: true,
                      file,
                    }));
                  }}
                  style={{ width: "25px" }}
                >
                  <FileIcon
                    extension={fileExtension}
                    {...customFileIconStyles[fileExtension as DefaultExtensionType]}
                    radius={3}
                  />
                </IconButton>
              )}

              <Typography
                sx={{
                  display: "WebkitBox",
                  WebkitLineClamp: "2",
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {file.name.replace(fileExtensionRegEx, "").replace(".", "")}
              </Typography>
            </Grid>

            {!documentIsReadOnly &&
              (!checkIfFileDeletable || (!!checkIfFileDeletable && checkIfFileDeletable(file))) && (
                <DeletButton
                  onClick={() => {
                    handleDeleteFile(file);
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </DeletButton>
              )}
          </Paper>
        );
      })}

      {!!state.file && (
        <Popover
          open={state.isViewingFile}
          anchorPosition={{ left: 0, top: 0 }}
          anchorReference={"anchorPosition"}
        >
          <IconButton
            onClick={() => {
              setState({
                ...state,
                isViewingFile: false,
                file: null,
              });
            }}
          >
            <StyledFontAwesomeIcon icon={faClose} />
          </IconButton>
          {state.file && (
            <DocumentViewer
              fileDto={state.file}
              isGenericDocument={isGenericDocument}
            />
          )}
        </Popover>
      )}
    </Container>
  );
};

export default SimpleDocumentView;
